import { Fragment, useState, useEffect, useRef } from 'react';
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch';
import Scrollbar from '../../components/scrollbar/scrollbar';
import PCICLogo from './pcic-new-logo.jpg';
import './WallOfAkhirah.css';

const TOTAL_TILES = 500;
const TILE_PRICE = 3500;
const RESERVED_ROWS = 3; // First 3 rows are reserved for admin
const TILES_PER_ROW = 25;
const RESERVED_TILES_COUNT = RESERVED_ROWS * TILES_PER_ROW; // Tiles 1-75 are reserved

// Animated Counter Component
const AnimatedCounter = ({ value, prefix = '', suffix = '', duration = 2000, ready = true }) => {
    const [displayValue, setDisplayValue] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const counterRef = useRef(null);

    // Track visibility with intersection observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.5 }
        );

        if (counterRef.current) {
            observer.observe(counterRef.current);
        }

        return () => observer.disconnect();
    }, []);

    // Animate when visible AND ready (data loaded)
    useEffect(() => {
        if (isVisible && ready && !hasAnimated) {
            setHasAnimated(true);
            animateValue(0, value, duration);
        }
    }, [isVisible, ready, hasAnimated, value, duration]);

    const animateValue = (start, end, animDuration) => {
        const startTime = performance.now();
        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / animDuration, 1);
            const easeOut = 1 - Math.pow(1 - progress, 3);
            const current = Math.floor(start + (end - start) * easeOut);
            setDisplayValue(current);
            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };
        requestAnimationFrame(animate);
    };

    return (
        <span ref={counterRef}>
            {prefix}{displayValue.toLocaleString()}{suffix}
        </span>
    );
};

// Zoom Controls Component
const ZoomControls = ({ zoomLevel }) => {
    const { zoomIn, zoomOut, resetTransform } = useControls();

    return (
        <div className="zoom-controls">
            <button
                className="zoom-btn"
                onClick={() => zoomIn()}
                aria-label="Zoom in"
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="M21 21l-4.35-4.35"/>
                    <path d="M11 8v6M8 11h6"/>
                </svg>
            </button>
            <div className="zoom-level">{Math.round(zoomLevel * 100)}%</div>
            <button
                className="zoom-btn"
                onClick={() => zoomOut()}
                aria-label="Zoom out"
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="M21 21l-4.35-4.35"/>
                    <path d="M8 11h6"/>
                </svg>
            </button>
            <button
                className="zoom-btn zoom-btn-reset"
                onClick={() => resetTransform()}
                aria-label="Reset zoom"
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                    <path d="M3 3v5h5"/>
                </svg>
            </button>
        </div>
    );
};

const WallOfAkhirah = () => {
    const [tiles, setTiles] = useState([]);
    const [reservedTiles, setReservedTiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
    claimed: 0,
    reserved: RESERVED_TILES_COUNT, // First 75 tiles are always reserved
    available: TOTAL_TILES - RESERVED_TILES_COUNT // Only public tiles (76-500) are available
});
    const [zoomLevel, setZoomLevel] = useState(1);
    const [selectedTile, setSelectedTile] = useState(null);
    const [cartTiles, setCartTiles] = useState([]); // Cart for multiple tile selection
    const [showCheckoutModal, setShowCheckoutModal] = useState(false);
    const [tilesRevealed, setTilesRevealed] = useState(false);
    const [titleRevealed, setTitleRevealed] = useState(false);
    const [donorName, setDonorName] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [donorEmail, setDonorEmail] = useState('');
    const [donorPhone, setDonorPhone] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [nameError, setNameError] = useState('');
    const [paymentMessage, setPaymentMessage] = useState(null);
    const [paymentPlan, setPaymentPlan] = useState('full'); // 'full', '3', '6', '12'
    const [inquirySubmitted, setInquirySubmitted] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const wallSectionRef = useRef(null);
    const titleRef = useRef(null);
    const gridRef = useRef(null);
    const statsRef = useRef(null);
    const statsSectionRef = useRef(null);

    // Handle URL parameters for payment success/cancel
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const success = params.get('success');
        const canceled = params.get('canceled');
        // Support both old 'tile' param and new 'tiles' param (comma-separated)
        const tilesParam = params.get('tiles') || params.get('tile');
        const sessionId = params.get('session_id');

        // Parse tile numbers from param
        const tileNumbers = tilesParam ? tilesParam.split(',').map(n => n.trim()).filter(n => n) : [];
        const tileCount = tileNumbers.length;
        const tileDisplay = tileCount === 1
            ? `Tile #${tileNumbers[0]}`
            : `${tileCount} Tiles (#${tileNumbers.join(', #')})`;

        if (success === 'true' && tileCount > 0 && sessionId) {
            // Verify payment with Stripe and update database
            const verifyPayment = async () => {
                try {
                    const response = await fetch('/api/wall-tiles/verify-payment', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ session_id: sessionId })
                    });
                    const data = await response.json();

                    if (data.success && data.status === 'paid') {
                        const savedCount = data.tiles_saved?.length || tileCount;
                        setPaymentMessage({
                            type: 'success',
                            title: 'Payment Successful!',
                            message: `Thank you for claiming ${savedCount === 1 ? `Tile #${data.tiles_saved?.[0] || tileNumbers[0]}` : `${savedCount} tiles`}! Your payment has been received. Our team will verify your identity, and your name will be displayed on the tile${savedCount > 1 ? 's' : ''} once confirmed. May Allah reward you abundantly for your contribution.`
                        });
                    } else {
                        setPaymentMessage({
                            type: 'success',
                            title: 'Payment Processing',
                            message: `Your payment for ${tileDisplay} is being processed. Once completed, our team will verify your identity before displaying your name on the tile${tileCount > 1 ? 's' : ''}.`
                        });
                    }
                } catch (error) {
                    console.error('Error verifying payment:', error);
                    setPaymentMessage({
                        type: 'success',
                        title: 'Payment Received',
                        message: `Thank you for claiming ${tileDisplay}. Your payment is being processed and our team will verify your identity shortly.`
                    });
                }
                // Refresh tiles after verification
                fetchTiles();
                // Clear cart after successful payment
                setCartTiles([]);
            };
            verifyPayment();
            // Clean up URL
            window.history.replaceState({}, '', window.location.pathname);
        } else if (canceled === 'true' && tileCount > 0) {
            setPaymentMessage({
                type: 'canceled',
                title: 'Payment Canceled',
                message: `Your payment for ${tileDisplay} was canceled. The tile${tileCount > 1 ? 's are' : ' is'} still available if you'd like to try again.`
            });
            // Clean up URL
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);

    useEffect(() => {
        fetchTiles();
    }, []);

    // Set body and html background to transparent for this page
    useEffect(() => {
        const originalBodyBg = document.body.style.backgroundColor;
        const originalHtmlBg = document.documentElement.style.backgroundColor;
        document.body.style.setProperty('background-color', 'transparent', 'important');
        document.documentElement.style.setProperty('background-color', 'transparent', 'important');
        return () => {
            document.body.style.backgroundColor = originalBodyBg;
            document.documentElement.style.backgroundColor = originalHtmlBg;
        };
    }, []);

    // Scroll-based motion effects
    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            const windowHeight = window.innerHeight;

            // Calculate scroll progress (0 to 1) for the first viewport
            const progress = Math.min(currentScrollY / windowHeight, 1);
            setScrollProgress(progress);

            // Zoom effect for fixed background (zoom in as you scroll, no vertical movement)
            const fixedBg = document.querySelector('.hero-fixed-bg');
            if (fixedBg) {
                // Start at scale 1, zoom to 1.4 as user scrolls through first viewport
                const zoomScale = 1 + (progress * 0.4);
                fixedBg.style.transform = `scale(${zoomScale})`;
            }

            // Title transition from vertical to horizontal - gradual based on scroll
            const headerContainer = document.querySelector('.wall-header-container');

            if (headerContainer) {
                // Set CSS custom property for gradual animation (0 to 1)
                const transitionProgress = Math.max(0, Math.min((progress - 0.15) / 0.35, 1));
                headerContainer.style.setProperty('--scroll-progress', transitionProgress);

                // Add class when scrolled enough to trigger horizontal layout
                if (progress > 0.2) {
                    headerContainer.classList.add('title-horizontal');
                } else {
                    headerContainer.classList.remove('title-horizontal');
                }

                // Show sticky title when scrolled past header
                if (progress > 0.5) {
                    headerContainer.classList.add('title-sticky');
                } else {
                    headerContainer.classList.remove('title-sticky');
                }
            }

            // Border expansion progress for tile container
            const galleryFrame = document.querySelector('.gallery-frame');
            if (galleryFrame) {
                const borderProgress = Math.max(0, Math.min((progress - 0.4) / 0.4, 1));
                galleryFrame.style.setProperty('--border-progress', borderProgress);
            }

            // Reveal elements on scroll
            const revealElements = document.querySelectorAll('.scroll-reveal');
            revealElements.forEach(el => {
                const rect = el.getBoundingClientRect();
                if (rect.top < windowHeight * 0.85) {
                    el.classList.add('revealed');
                }
            });

            // Title reveal animation - trigger when title section comes into view
            if (titleRef.current && !titleRevealed) {
                const titleRect = titleRef.current.getBoundingClientRect();
                if (titleRect.top < windowHeight * 0.8) {
                    setTitleRevealed(true);
                    titleRef.current.classList.add('title-revealed');
                }
            }

            // Tile grid reveal animation
            if (gridRef.current && !tilesRevealed) {
                const gridRect = gridRef.current.getBoundingClientRect();
                if (gridRect.top < windowHeight * 0.75) {
                    setTilesRevealed(true);
                    gridRef.current.classList.add('tiles-revealed');
                }
            }

            // Sticky stats detection
            if (statsRef.current) {
                const statsRect = statsRef.current.getBoundingClientRect();
                if (statsRect.top <= 80) {
                    statsRef.current.classList.add('stuck');
                } else {
                    statsRef.current.classList.remove('stuck');
                }
            }

        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll(); // Initial call

        return () => window.removeEventListener('scroll', handleScroll);
    }, [tilesRevealed]);

    const fetchTiles = async () => {
        try {
            // Add cache-busting timestamp to prevent browser/CDN caching
            const response = await fetch(`/api/wall-tiles?all=true&_t=${Date.now()}`);

            if (response.ok) {
                const data = await response.json();
                const allTiles = Array.isArray(data) ? data : [];
                const confirmedTiles = allTiles.filter(t => t.status === 'confirmed');
                const pendingTiles = allTiles.filter(t => t.status === 'pending');

                setTiles(confirmedTiles);
                setReservedTiles(pendingTiles);

                // Calculate stats based on actual database data
                const publicTilesTotal = TOTAL_TILES - RESERVED_TILES_COUNT;
                const claimedAdminTiles = confirmedTiles.filter(t => t.tile_number <= RESERVED_TILES_COUNT).length;
                const claimedPublicTiles = confirmedTiles.filter(t => t.tile_number > RESERVED_TILES_COUNT).length;
                const totalClaimed = claimedAdminTiles + claimedPublicTiles;
                const pendingPublicTiles = pendingTiles.filter(t => t.tile_number > RESERVED_TILES_COUNT).length;
                const reservedAdminTiles = RESERVED_TILES_COUNT - claimedAdminTiles;
                const totalReserved = reservedAdminTiles + pendingPublicTiles;
                const availablePublicTiles = publicTilesTotal - claimedPublicTiles - pendingPublicTiles;

                setStats({
                    claimed: totalClaimed,
                    reserved: totalReserved,
                    available: availablePublicTiles
                });
            }
        } catch (error) {
            // Set default stats on error
            setStats({
                claimed: 0,
                reserved: RESERVED_TILES_COUNT,
                available: TOTAL_TILES - RESERVED_TILES_COUNT
            });
        }
        setLoading(false);
    };

    const scrollToStats = () => {
        if (!statsSectionRef.current) return;

        const targetPosition = statsSectionRef.current.getBoundingClientRect().top + window.pageYOffset;
        const startPosition = window.pageYOffset;
        const distance = targetPosition - startPosition;
        const duration = 1200;
        let startTime = null;

        const easeInOutCubic = (t) => {
            return t < 0.5
                ? 4 * t * t * t
                : 1 - Math.pow(-2 * t + 2, 3) / 2;
        };

        const animation = (currentTime) => {
            if (startTime === null) startTime = currentTime;
            const timeElapsed = currentTime - startTime;
            const progress = Math.min(timeElapsed / duration, 1);
            const ease = easeInOutCubic(progress);

            window.scrollTo(0, startPosition + distance * ease);

            if (timeElapsed < duration) {
                requestAnimationFrame(animation);
            }
        };

        requestAnimationFrame(animation);
    };

    const handleTileClick = (tileNumber, tile, isConfirmed, isReserved = false, isAdminReserved = false) => {
        setSelectedTile({
            number: tileNumber,
            donorName: tile?.donor_name || null,
            isConfirmed,
            isReserved,
            isAdminReserved
        });
    };

    const closeTileModal = () => {
        setSelectedTile(null);
        setDonorName('');
        setDisplayName('');
        setDonorEmail('');
        setDonorPhone('');
        setEmailError('');
        setPhoneError('');
        setNameError('');
    };

    const validateEmail = (email) => {
        if (!email.trim()) {
            setEmailError('');
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setEmailError('Please enter a valid email address');
        } else {
            setEmailError('');
        }
    };

    const validatePhone = (phone) => {
        if (!phone.trim()) {
            setPhoneError('');
            return;
        }
        // Allow digits, spaces, dashes, parentheses, and plus sign. Minimum 8 digits.
        const digitsOnly = phone.replace(/\D/g, '');
        if (digitsOnly.length < 8) {
            setPhoneError('Please enter a valid phone number (minimum 8 digits)');
        } else if (digitsOnly.length > 15) {
            setPhoneError('Phone number is too long');
        } else {
            setPhoneError('');
        }
    };

    const validateName = (name) => {
        if (!name.trim()) {
            setNameError('');
            return;
        }
        // Name should be at least 2 characters and contain only letters, spaces, hyphens, and apostrophes
        const trimmedName = name.trim();
        if (trimmedName.length < 2) {
            setNameError('Name must be at least 2 characters');
        } else if (!/^[a-zA-Z\s'-]+$/.test(trimmedName)) {
            setNameError('Name can only contain letters, spaces, hyphens, and apostrophes');
        } else {
            setNameError('');
        }
    };

    const closePaymentMessage = () => {
        setPaymentMessage(null);
        fetchTiles(); // Refresh tiles after payment
    };

    // Cart functions
    const addToCart = (tileNumber) => {
        if (!cartTiles.includes(tileNumber)) {
            setCartTiles([...cartTiles, tileNumber].sort((a, b) => a - b));
        }
    };

    const removeFromCart = (tileNumber) => {
        setCartTiles(cartTiles.filter(t => t !== tileNumber));
    };

    const clearCart = () => {
        setCartTiles([]);
    };

    const isInCart = (tileNumber) => {
        return cartTiles.includes(tileNumber);
    };

    const cartTotal = cartTiles.length * TILE_PRICE;

    const handleCheckout = () => {
        if (cartTiles.length === 0) return;
        setShowCheckoutModal(true);
    };

    const closeCheckoutModal = () => {
        setShowCheckoutModal(false);
        setDonorName('');
        setDisplayName('');
        setDonorEmail('');
        setDonorPhone('');
        setEmailError('');
        setPhoneError('');
        setNameError('');
    };

    const handleSubmitInquiry = async (e) => {
        e.preventDefault();

        if (!donorName.trim() || !displayName.trim() || !donorEmail.trim() || !donorPhone.trim()) {
            alert('Please fill in all required fields');
            return;
        }

        // Validate name before submitting
        const trimmedName = donorName.trim();
        if (trimmedName.length < 2) {
            setNameError('Name must be at least 2 characters');
            return;
        }
        if (!/^[a-zA-Z\s'-]+$/.test(trimmedName)) {
            setNameError('Name can only contain letters, spaces, hyphens, and apostrophes');
            return;
        }

        // Validate email before submitting
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(donorEmail.trim())) {
            setEmailError('Please enter a valid email address');
            return;
        }

        // Validate phone before submitting
        const digitsOnly = donorPhone.replace(/\D/g, '');
        if (digitsOnly.length < 8) {
            setPhoneError('Please enter a valid phone number (minimum 8 digits)');
            return;
        }
        if (digitsOnly.length > 15) {
            setPhoneError('Phone number is too long');
            return;
        }

        setIsProcessing(true);

        // Use cart tiles if checkout modal is open, otherwise use selected tile
        const tilesToInquire = showCheckoutModal ? cartTiles : [selectedTile.number];

        try {
            const response = await fetch('/api/wall-tiles/inquiry', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    tile_numbers: tilesToInquire,
                    donor_name: displayName.trim(),
                    donor_full_name: donorName.trim(),
                    donor_email: donorEmail.trim(),
                    donor_phone: donorPhone.trim(),
                    payment_plan: paymentPlan,
                }),
            });

            const data = await response.json();

            if (data.error) {
                alert(data.error);
                setIsProcessing(false);
                return;
            }

            // Show success state
            setInquirySubmitted(true);
            setIsProcessing(false);
        } catch (error) {
            alert('An error occurred. Please try again.');
            setIsProcessing(false);
        }
    };

    const closeInquirySuccess = () => {
        setInquirySubmitted(false);
        setShowCheckoutModal(false);
        setDonorName('');
        setDisplayName('');
        setDonorEmail('');
        setDonorPhone('');
        setPaymentPlan('full');
        setCartTiles([]);
        fetchTiles(); // Refresh tiles
    };

    const generateTileGrid = () => {
        const tileElements = [];
        const confirmedTileNumbers = new Set(tiles.map(t => t.tile_number));
        const reservedTileNumbers = new Set(reservedTiles.map(t => t.tile_number));

        for (let i = 1; i <= TOTAL_TILES; i++) {
            const confirmedTile = tiles.find(t => t.tile_number === i);
            const reservedTile = reservedTiles.find(t => t.tile_number === i);
            const isConfirmed = confirmedTileNumbers.has(i);
            const isReserved = reservedTileNumbers.has(i);
            const tile = confirmedTile || reservedTile;

            // Check if this tile is in the first 3 rows (admin reserved)
            const isAdminReserved = i <= RESERVED_TILES_COUNT;

            let tileClass = 'tile-available';
            let ariaLabel = `Tile ${i} - Available`;

            if (isConfirmed) {
                tileClass = 'tile-sold';
                ariaLabel = `Tile ${i} - Claimed by ${tile?.donor_name || 'Anonymous'}`;
            } else if (isReserved) {
                tileClass = 'tile-reserved';
                ariaLabel = `Tile ${i} - Reserved`;
            } else if (isAdminReserved) {
                // Admin reserved tiles (first 3 rows) - not available for public purchase
                // Use same style as pending/reserved tiles (orange)
                tileClass = 'tile-reserved';
                ariaLabel = `Tile ${i} - Reserved`;
            }

            // Determine if tile should be clickable
            const isClickable = !isAdminReserved || isConfirmed || isReserved;

            const tileInCart = isInCart(i);

            tileElements.push(
                <div
                    key={i}
                    className={`wall-tile ${tileClass} ${zoomLevel > 1.5 ? 'zoomed' : ''} ${tileInCart ? 'tile-in-cart' : ''}`}
                    onClick={() => isClickable ? handleTileClick(i, tile, isConfirmed, isReserved, isAdminReserved) : null}
                    role="button"
                    tabIndex={isClickable ? 0 : -1}
                    aria-label={ariaLabel}
                    style={!isClickable ? { cursor: 'default' } : {}}
                >
                    <div className="tile-content">
                        {tileInCart ? (
                            <>
                                <span className="tile-cart-icon">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M20 6L9 17l-5-5"/>
                                    </svg>
                                </span>
                                <span className="tile-full-name">In Cart</span>
                            </>
                        ) : isConfirmed && tile?.donor_name ? (
                            <>
                                <span className="tile-initial">{tile.donor_name.charAt(0)}</span>
                                <span className="tile-full-name">{tile.donor_name}</span>
                            </>
                        ) : isReserved ? (
                            <>
                                <span className="tile-initial">⏳</span>
                                <span className="tile-full-name">Pending</span>
                            </>
                        ) : isAdminReserved ? (
                            <>
                                <span className="tile-initial">⏳</span>
                                <span className="tile-full-name">Reserved</span>
                            </>
                        ) : (
                            <>
                                <span className="tile-number">{i}</span>
                                <span className="tile-status">Available</span>
                            </>
                        )}
                    </div>
                </div>
            );
        }
        return tileElements;
    };

    return (
        <Fragment>
            <div className="wall-of-akhirah-page">
                {/* Fixed Background */}
                <div className="hero-fixed-bg"></div>

                {/* Wall Section */}
                <section className="wall-section" ref={wallSectionRef}>
                    {/* Header with Mosque Brand and Title */}
                    <div className="wall-header-container" ref={titleRef}>
                        {/* PCIC Logo */}
                        <div className="pcic-logo-container animate-fade-up">
                            <img src={PCICLogo} alt="Point Cook Islamic Community Logo" className="pcic-logo" />
                        </div>

                        {/* Mosque Identity */}
                        <div className="mosque-brand animate-fade-up">
                            <span className="brand-text">Point Cook Islamic Community</span>
                        </div>

                        {/* Intro Text */}
                        <div className="wall-intro animate-fade-up delay-1">
                            <p className="intro-text">
                                We are building Point Cook's 1<sup>st</sup> Masjid and offering you an opportunity to put your name on the Wall of Akhirah.
                            </p>
                        </div>

                        {/* Wall of Akhirah Title */}
                        <div className="wall-title-container">
                            <h1 className="wall-title-main">Wall of Akhirah</h1>
                        </div>

                        {/* Description */}
                        <div className="wall-description animate-fade-up delay-2">
                            <p className="description-text">
                                The entrance wall of the Masjid will be called the Wall of Akhirah and will have 500 golden tiles with the names of the generous donors. Each tile can be reserved for $3500. If anyone prefers anonymity, they can nominate names from the noble family of Rasool Allah (SAW) or his noble companions.
                            </p>
                        </div>

                        {/* Hadith */}
                        <div className="wall-verse animate-fade-up delay-2">
                            <p className="verse-text">
                                "Whoever builds a mosque for Allah, Allah would build a house for him like it in Paradise."
                            </p>
                            <p className="verse-reference">(Sahih Muslim 533b [Book 5, Hadith 30] & Sahih al-Bukhari 450 [Book 8, Hadith 99])</p>
                        </div>

                        {/* Stats Section - in hero area */}
                        <div className="wall-stats hero-stats" ref={statsRef}>
                            <div className="stat-item">
                                <span className="stat-value">
                                    <AnimatedCounter value={TILE_PRICE} prefix="" suffix=" AUD" duration={1500} />
                                </span>
                                <span className="stat-label">PER TILE (AUD)</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-value">
                                    <AnimatedCounter value={stats.claimed} duration={1500} ready={!loading} />
                                </span>
                                <span className="stat-label">CLAIMED</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-value">
                                    <AnimatedCounter value={stats.reserved} duration={1500} ready={!loading} />
                                </span>
                                <span className="stat-label">RESERVED</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-value">
                                    <AnimatedCounter value={stats.available} duration={1500} ready={!loading} />
                                </span>
                                <span className="stat-label">AVAILABLE</span>
                            </div>
                        </div>

                        {/* Scroll Indicator */}
                        <div className="scroll-indicator animate-fade-up delay-3" onClick={scrollToStats}>
                            <div className="scroll-mouse">
                                <div className="scroll-wheel"></div>
                            </div>
                            <span className="scroll-text">SCROLL TO RESERVE YOUR TILE</span>
                        </div>
                    </div>

                    {/* Gallery Section */}
                    <div className="stats-section" ref={statsSectionRef}>
                    {/* Legend */}
                    <div className="wall-legend">
                        <div className="legend-item">
                            <span className="legend-tile legend-available"></span>
                            <span>Available</span>
                        </div>
                        <div className="legend-item">
                            <span className="legend-tile legend-reserved"></span>
                            <span>Reserved</span>
                        </div>
                        <div className="legend-item">
                            <span className="legend-tile legend-claimed"></span>
                            <span>Claimed</span>
                        </div>
                    </div>

                    {/* Modern Gallery Grid */}
                    <div className="gallery-container" ref={gridRef}>
                        <div className="gallery-frame">
                            {loading ? (
                                <div className="loading-spinner">Loading tiles...</div>
                            ) : (
                                <TransformWrapper
                                initialScale={1}
                                minScale={0.5}
                                maxScale={4}
                                centerOnInit={true}
                                wheel={{ step: 0.1 }}
                                pinch={{ step: 5 }}
                                doubleClick={{ mode: "zoomIn", step: 0.7 }}
                                onTransformed={(ref) => {
                                    setZoomLevel(ref.state.scale);
                                }}
                            >
                                {() => (
                                    <>
                                        <ZoomControls zoomLevel={zoomLevel} />
                                        <div className={`wall-grid-wrapper${cartTiles.length > 0 ? ' cart-visible' : ''}`}>
                                            <TransformComponent
                                                wrapperStyle={{
                                                    width: '100%',
                                                    height: '100%',
                                                    overflow: 'hidden'
                                                }}
                                                contentStyle={{
                                                    width: '100%',
                                                    height: '100%'
                                                }}
                                            >
                                                <div className="wall-grid">
                                                    {generateTileGrid()}
                                                </div>
                                            </TransformComponent>
                                        </div>
                                    </>
                                )}
                            </TransformWrapper>
                            )}
                        </div>

                        {/* Gallery Instructions */}
                        <div className="gallery-instructions">
                            <span className="instruction-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                    <path d="M9 12h6M12 9v6"/>
                                </svg>
                            </span>
                            <span>Pinch or scroll to zoom • Drag to explore</span>
                        </div>
                    </div>
                    </div>

                </section>

                {/* Payment Success/Cancel Message Modal */}
                {paymentMessage && (
                    <div className="tile-modal-overlay" onClick={closePaymentMessage}>
                        <div className="tile-modal payment-message-modal" onClick={(e) => e.stopPropagation()}>
                            <button className="tile-modal-close" onClick={closePaymentMessage}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 6L6 18M6 6l12 12"/>
                                </svg>
                            </button>
                            <div className={`tile-modal-icon ${paymentMessage.type}`}>
                                {paymentMessage.type === 'success' ? (
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20 6L9 17l-5-5"/>
                                    </svg>
                                ) : (
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M18 6L6 18M6 6l12 12"/>
                                    </svg>
                                )}
                            </div>
                            <h3 className="tile-modal-title">{paymentMessage.title}</h3>
                            <p className="tile-modal-message">{paymentMessage.message}</p>
                            <button className="btn-primary tile-modal-btn" onClick={closePaymentMessage}>
                                Continue
                            </button>
                        </div>
                    </div>
                )}

                {/* Tile Detail Modal */}
                {selectedTile && (
                    <div className="tile-modal-overlay" onClick={closeTileModal}>
                        <div className="tile-modal" onClick={(e) => e.stopPropagation()}>
                            <button className="tile-modal-close" onClick={closeTileModal}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 6L6 18M6 6l12 12"/>
                                </svg>
                            </button>
                            <div className={`tile-modal-icon ${selectedTile.isConfirmed ? 'claimed' : selectedTile.isReserved ? 'reserved' : 'available'}`}>
                                {(selectedTile.isConfirmed || selectedTile.isReserved) ? (
                                    <span>{selectedTile.donorName?.charAt(0) || '?'}</span>
                                ) : (
                                    <span>{selectedTile.number}</span>
                                )}
                            </div>
                            <h3 className="tile-modal-title">Tile #{selectedTile.number}</h3>
                            {selectedTile.isConfirmed ? (
                                <>
                                    <p className="tile-modal-status claimed">Claimed</p>
                                    <p className="tile-modal-donor">
                                        Donated by: <strong>{selectedTile.donorName || 'Anonymous'}</strong>
                                    </p>
                                    <p className="tile-modal-message">
                                        May Allah reward them abundantly for their contribution.
                                    </p>
                                </>
                            ) : selectedTile.isReserved ? (
                                <>
                                    <p className="tile-modal-status reserved">Reserved</p>
                                    <p className="tile-modal-message vetting-message">
                                        This tile has been claimed and is currently undergoing our vetting process to confirm the donor's identity. The donor's name will be displayed once verification is complete.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p className="tile-modal-status available">Available</p>
                                    <p className="tile-modal-price">{TILE_PRICE.toLocaleString()} AUD</p>
                                    <p className="tile-modal-message">
                                        Secure this tile and become part of our mosque's legacy.
                                    </p>
                                    {isInCart(selectedTile.number) ? (
                                        <div className="tile-cart-actions">
                                            <p className="tile-in-cart-message">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M20 6L9 17l-5-5"/>
                                                </svg>
                                                This tile is in your cart
                                            </p>
                                            <button
                                                className="btn-secondary tile-modal-btn"
                                                onClick={() => {
                                                    removeFromCart(selectedTile.number);
                                                    closeTileModal();
                                                }}
                                            >
                                                Remove from Cart
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            className="btn-primary tile-modal-btn"
                                            onClick={() => {
                                                addToCart(selectedTile.number);
                                                closeTileModal();
                                            }}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="9" cy="21" r="1"/>
                                                <circle cx="20" cy="21" r="1"/>
                                                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                                            </svg>
                                            Add to Cart
                                        </button>
                                    )}
                                    <p className="tile-modal-secure">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                            <path d="M7 11V7a5 5 0 0110 0v4"/>
                                        </svg>
                                        Secure payment powered by Stripe
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                )}

            </div>

            {/* Sticky Cart Footer */}
            {cartTiles.length > 0 && (
                <div className="cart-footer">
                    <div className="cart-footer-content">
                        <div className="cart-info">
                            <div className="cart-tiles">
                                <span className="cart-icon">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="9" cy="21" r="1"/>
                                        <circle cx="20" cy="21" r="1"/>
                                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                                    </svg>
                                    <span className="cart-count">{cartTiles.length}</span>
                                </span>
                                <div className="cart-tile-numbers">
                                    {cartTiles.length <= 5 ? (
                                        cartTiles.map((num, idx) => (
                                            <span key={num} className="cart-tile-badge">
                                                #{num}
                                                <button
                                                    className="cart-tile-remove"
                                                    onClick={() => removeFromCart(num)}
                                                    aria-label={`Remove tile ${num}`}
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))
                                    ) : (
                                        <>
                                            {cartTiles.slice(0, 3).map(num => (
                                                <span key={num} className="cart-tile-badge">
                                                    #{num}
                                                    <button
                                                        className="cart-tile-remove"
                                                        onClick={() => removeFromCart(num)}
                                                        aria-label={`Remove tile ${num}`}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                            <span className="cart-tile-more">+{cartTiles.length - 3} more</span>
                                        </>
                                    )}
                                </div>
                            </div>
                            <button className="cart-clear" onClick={clearCart}>
                                Clear All
                            </button>
                        </div>
                        <div className="cart-checkout">
                            <div className="cart-total">
                                <span className="cart-total-label">Total:</span>
                                <span className="cart-total-amount">${cartTotal.toLocaleString()} AUD</span>
                            </div>
                            <button className="cart-pay-btn" onClick={handleCheckout}>
                                Reserve Now
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M5 12h14M12 5l7 7-7 7"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Checkout Modal - Inquiry Form */}
            {showCheckoutModal && (
                <div className="tile-modal-overlay" onClick={inquirySubmitted ? closeInquirySuccess : closeCheckoutModal}>
                    <div className="tile-modal checkout-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="tile-modal-close" onClick={inquirySubmitted ? closeInquirySuccess : closeCheckoutModal}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                        </button>

                        {inquirySubmitted ? (
                            /* Success Message */
                            <>
                                <div className="tile-modal-icon success">
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20 6L9 17l-5-5"/>
                                    </svg>
                                </div>
                                <h3 className="tile-modal-title">JazakAllahu Khairan!</h3>
                                <div className="inquiry-success-message">
                                    <p className="success-main">
                                        Thank you for your interest in supporting Point Cook's first Masjid. Your inquiry has been received, and we truly appreciate your generous intention.
                                    </p>
                                    <p className="success-detail">
                                        A member of the PCIC team will be in touch with you shortly to discuss the next steps and payment arrangements.
                                    </p>
                                    <p className="success-dua">
                                        May Allah accept your contribution and reward you abundantly in this life and the Hereafter. Your support brings us one step closer to establishing a place of worship for our community.
                                    </p>
                                </div>
                                <button className="btn-primary tile-modal-btn" onClick={closeInquirySuccess}>
                                    Close
                                </button>
                            </>
                        ) : (
                            /* Inquiry Form */
                            <>
                                <div className="tile-modal-icon checkout">
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                                        <path d="M2 17l10 5 10-5"/>
                                        <path d="M2 12l10 5 10-5"/>
                                    </svg>
                                </div>
                                <h3 className="tile-modal-title">Reserve Your Tile{cartTiles.length > 1 ? 's' : ''}</h3>

                                <div className="checkout-summary">
                                    <p className="checkout-tiles-count">{cartTiles.length} Tile{cartTiles.length > 1 ? 's' : ''} Selected</p>
                                    <div className="checkout-tiles-list">
                                        {cartTiles.map(num => (
                                            <span key={num} className="checkout-tile-num">#{num}</span>
                                        ))}
                                    </div>
                                    <p className="checkout-total">${cartTotal.toLocaleString()} AUD</p>
                                </div>

                                {/* Payment Integration Notice */}
                                <div className="payment-notice">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"/>
                                        <path d="M12 16v-4M12 8h.01"/>
                                    </svg>
                                    <p>Online payment will be available soon. Please submit your inquiry below, and our team will contact you to arrange payment.</p>
                                </div>

                                <form className="tile-claim-form" onSubmit={handleSubmitInquiry}>
                                    <div className="form-section-title">Your Contact Details</div>
                                    <div className="form-group">
                                        <label htmlFor="donorName">Full Name</label>
                                        <input
                                            type="text"
                                            id="donorName"
                                            value={donorName}
                                            onChange={(e) => {
                                                setDonorName(e.target.value);
                                                if (nameError) validateName(e.target.value);
                                            }}
                                            onBlur={(e) => validateName(e.target.value)}
                                            placeholder="Enter your full name"
                                            required
                                            disabled={isProcessing}
                                            className={nameError ? 'input-error' : ''}
                                        />
                                        {nameError && <span className="error-message">{nameError}</span>}
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="donorEmail">Email Address</label>
                                        <input
                                            type="email"
                                            id="donorEmail"
                                            value={donorEmail}
                                            onChange={(e) => {
                                                setDonorEmail(e.target.value);
                                                if (emailError) validateEmail(e.target.value);
                                            }}
                                            onBlur={(e) => validateEmail(e.target.value)}
                                            placeholder="Enter your email"
                                            required
                                            disabled={isProcessing}
                                            className={emailError ? 'input-error' : ''}
                                        />
                                        {emailError && <span className="error-message">{emailError}</span>}
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="donorPhone">Phone Number</label>
                                        <input
                                            type="tel"
                                            id="donorPhone"
                                            value={donorPhone}
                                            onChange={(e) => {
                                                setDonorPhone(e.target.value);
                                                if (phoneError) validatePhone(e.target.value);
                                            }}
                                            onBlur={(e) => validatePhone(e.target.value)}
                                            placeholder="Enter your phone number"
                                            required
                                            disabled={isProcessing}
                                            className={phoneError ? 'input-error' : ''}
                                        />
                                        {phoneError && <span className="error-message">{phoneError}</span>}
                                    </div>

                                    <div className="form-section-title">Tile Display</div>
                                    <div className="form-group">
                                        <label htmlFor="displayName">Name to Display on Tile{cartTiles.length > 1 ? 's' : ''}</label>
                                        <input
                                            type="text"
                                            id="displayName"
                                            value={displayName}
                                            onChange={(e) => setDisplayName(e.target.value)}
                                            placeholder="e.g., The Ahmed Family, In Memory of..."
                                            required
                                            disabled={isProcessing}
                                        />
                                        <span className="form-hint">This name will appear on your tile{cartTiles.length > 1 ? 's' : ''}. You may use your name, family name, or dedicate it in memory of a loved one.</span>
                                    </div>

                                    <div className="form-section-title">Payment Preference</div>
                                    <div className="payment-plan-options">
                                        <label className={`payment-plan-option ${paymentPlan === 'full' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="paymentPlan"
                                                value="full"
                                                checked={paymentPlan === 'full'}
                                                onChange={(e) => setPaymentPlan(e.target.value)}
                                                disabled={isProcessing}
                                            />
                                            <span className="plan-radio"></span>
                                            <div className="plan-details">
                                                <span className="plan-name">Full Payment</span>
                                                <span className="plan-amount">${cartTotal.toLocaleString()} AUD</span>
                                            </div>
                                        </label>

                                        <label className={`payment-plan-option ${paymentPlan === '2' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="paymentPlan"
                                                value="2"
                                                checked={paymentPlan === '2'}
                                                onChange={(e) => setPaymentPlan(e.target.value)}
                                                disabled={isProcessing}
                                            />
                                            <span className="plan-radio"></span>
                                            <div className="plan-details">
                                                <span className="plan-name">2 Monthly Instalments</span>
                                                <span className="plan-amount">${Math.ceil(cartTotal / 2).toLocaleString()} AUD/month</span>
                                            </div>
                                        </label>

                                        <label className={`payment-plan-option ${paymentPlan === '5' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="paymentPlan"
                                                value="5"
                                                checked={paymentPlan === '5'}
                                                onChange={(e) => setPaymentPlan(e.target.value)}
                                                disabled={isProcessing}
                                            />
                                            <span className="plan-radio"></span>
                                            <div className="plan-details">
                                                <span className="plan-name">5 Monthly Instalments</span>
                                                <span className="plan-amount">${Math.ceil(cartTotal / 5).toLocaleString()} AUD/month</span>
                                            </div>
                                        </label>

                                        <label className={`payment-plan-option ${paymentPlan === '10' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="paymentPlan"
                                                value="10"
                                                checked={paymentPlan === '10'}
                                                onChange={(e) => setPaymentPlan(e.target.value)}
                                                disabled={isProcessing}
                                            />
                                            <span className="plan-radio"></span>
                                            <div className="plan-details">
                                                <span className="plan-name">10 Monthly Instalments</span>
                                                <span className="plan-amount">${Math.ceil(cartTotal / 10).toLocaleString()} AUD/month</span>
                                            </div>
                                        </label>

                                    </div>

                                    <button
                                        type="submit"
                                        className="btn-primary tile-modal-btn"
                                        disabled={isProcessing}
                                    >
                                        {isProcessing ? 'Submitting...' : 'Submit Inquiry'}
                                    </button>
                                </form>
                                <p className="tile-modal-info">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                                    </svg>
                                    Our team will contact you within 2-3 business days
                                </p>
                            </>
                        )}
                    </div>
                </div>
            )}

            <Scrollbar />
        </Fragment>
    );
};

export default WallOfAkhirah;
